import React, { useEffect, useRef, useState } from 'react';
import ContenidoBlock from './ContenidoBlock';
import {
  ContentDatos,
  ContentTemplateId,
  emptyDatos,
  exampleDatos,
  isContentTemplateId,
  remapDatos,
  validateDatos,
} from './templates';

const CONFIG = 'CONTENIDO_PREVIEW_CONFIG';
const READY = 'CONTENIDO_PREVIEW_READY';
const CHANGE = 'CONTENIDO_PREVIEW_CHANGE';
const VALIDATE = 'CONTENIDO_PREVIEW_VALIDATE';
const VALID = 'CONTENIDO_PREVIEW_VALID';

interface PreviewConfig {
  type: typeof CONFIG;
  epoch: number;
  templateId: string;
  datos: ContentDatos | null;
  templateChanged?: boolean;
  acento: string;
  animaciones: boolean;
  mode: 'edit' | 'preview';
  exampleToken: number;
  replayToken: number;
}

function allowedOrigins(): string[] {
  return (process.env.RAZZLE_PREVIEW_ALLOWED_ORIGINS || '')
    .split(',')
    .map((origin) => origin.trim())
    .filter(Boolean);
}

function resolveTargetOrigin(origins: string[]): string | null {
  if (document.referrer) {
    try {
      const referrerOrigin = new URL(document.referrer).origin;
      if (origins.includes(referrerOrigin)) return referrerOrigin;
    } catch {
      return origins[0] || null;
    }
  }
  return origins[0] || null;
}

function targetWindow(): Window | null {
  if (window.parent !== window) return window.parent;
  if (window.opener) return window.opener as Window;
  return null;
}

export default function ContenidoPreviewRoute() {
  const [templateId, setTemplateId] = useState<ContentTemplateId>('cifras');
  const [datos, setDatos] = useState<ContentDatos>(emptyDatos('cifras'));
  const [acento, setAcento] = useState('#f1c050');
  const [animaciones, setAnimaciones] = useState(true);
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  const [replayToken, setReplayToken] = useState(0);
  const [notice, setNotice] = useState('');
  const epochRef = useRef(0);
  const exampleRef = useRef(0);
  const templateRef = useRef<ContentTemplateId>('cifras');
  const datosRef = useRef(datos);
  datosRef.current = datos;

  useEffect(() => {
    const origins = allowedOrigins();
    const target = targetWindow();
    const origin = resolveTargetOrigin(origins);
    if (target && origin) target.postMessage({ type: READY }, origin);

    function onMessage(event: MessageEvent) {
      if (!origins.includes(event.origin)) return;
      const message = event.data;
      if (message?.type === VALIDATE) {
        const errors = validateDatos(templateRef.current, datosRef.current);
        if (target) {
          target.postMessage(
            { type: VALID, ok: errors.length === 0, errors, datos: datosRef.current },
            event.origin,
          );
        }
        const first = document.querySelector<HTMLElement>('.contenido-block [data-path]');
        if (errors.length && first) first.scrollIntoView({ block: 'center' });
        return;
      }
      if (message?.type !== CONFIG) return;
      const config = message as PreviewConfig;
      if (!isContentTemplateId(config.templateId)) return;
      setAcento(config.acento || '#f1c050');
      setAnimaciones(config.animaciones !== false);
      setMode(config.mode === 'preview' ? 'preview' : 'edit');
      if (config.replayToken !== undefined) setReplayToken(config.replayToken);
      if (config.exampleToken && config.exampleToken !== exampleRef.current) {
        exampleRef.current = config.exampleToken;
        const example = exampleDatos(config.templateId);
        templateRef.current = config.templateId;
        setTemplateId(config.templateId);
        setDatos(example);
        if (target)
          target.postMessage(
            { type: CHANGE, datos: example, templateId: config.templateId },
            event.origin,
          );
        return;
      }
      if (config.epoch === epochRef.current) return;
      epochRef.current = config.epoch;
      const nextDatos = config.templateChanged
        ? remapDatos(templateRef.current, config.templateId, config.datos || datosRef.current)
        : config.datos || emptyDatos(config.templateId);
      templateRef.current = config.templateId;
      setTemplateId(config.templateId);
      setDatos(nextDatos);
      if (config.templateChanged && target) {
        target.postMessage(
          { type: CHANGE, datos: nextDatos, templateId: config.templateId },
          event.origin,
        );
      }
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const postHeight = () => {
      const target = targetWindow();
      const origin = resolveTargetOrigin(allowedOrigins());
      if (!target || !origin) return;
      target.postMessage({ type: 'CONTENIDO_PREVIEW_HEIGHT', height: root.scrollHeight }, origin);
    };
    postHeight();
    const observer = new ResizeObserver(postHeight);
    observer.observe(root);
    return () => observer.disconnect();
  }, [datos, mode, templateId]);

  return (
    <>
      <ContenidoBlock
        acento={acento}
        animaciones={animaciones}
        datos={datos}
        mode={mode}
        onChange={(next) => {
          setDatos(next);
          const target = targetWindow();
          const origin = resolveTargetOrigin(allowedOrigins());
          if (target && origin)
            target.postMessage({ type: CHANGE, datos: next, templateId }, origin);
        }}
        onNotice={(message) => {
          setNotice(message);
          window.setTimeout(() => setNotice(''), 2200);
        }}
        replayToken={replayToken}
        templateId={templateId}
      />
      {notice ? <div className='contenido-notice'>{notice}</div> : null}
    </>
  );
}
